import { CaptureSearch } from './CaptureSearch.js';
import type { GameState } from './GameState.js';
import {
  ALL_DIAGONALS,
  inBounds,
  isPromotionSquare,
  offset,
  type Direction,
} from './path.js';
import type { RuleSet } from './rules/types.js';
import type { Move, Piece } from './types.js';

export class MoveValidator {
  private readonly captureSearch: CaptureSearch;

  constructor(private readonly ruleSet: RuleSet) {
    this.captureSearch = new CaptureSearch(ruleSet);
  }

  /**
   * Returns every legal move for the side to move. Captures are mandatory:
   * if any capture chain exists, only chains are returned. If the variant
   * sets `maxCaptureRequired`, the result is filtered to the longest chains.
   */
  legalMoves(state: GameState): Move[] {
    const captures = this.captureSearch.enumerate(state, state.currentPlayer);
    if (captures.length > 0) {
      if (!this.ruleSet.maxCaptureRequired) return captures;
      const max = captures.reduce((m, c) => Math.max(m, c.captures.length), 0);
      return captures.filter((c) => c.captures.length === max);
    }
    return this.quietMoves(state);
  }

  hasAnyMove(state: GameState): boolean {
    return this.legalMoves(state).length > 0;
  }

  private quietMoves(state: GameState): Move[] {
    const out: Move[] = [];
    const size = this.ruleSet.boardSize;
    for (const { square, piece } of state.pieces()) {
      if (piece.color !== state.currentPlayer) continue;
      const directions = this.directionsForQuietMove(piece);
      const flying = piece.kind === 'king' && this.ruleSet.flyingKings;
      for (const dir of directions) {
        const maxDistance = flying ? size : 1;
        for (let d = 1; d <= maxDistance; d += 1) {
          const dest = offset(square, dir, d);
          if (!inBounds(dest, size)) break;
          if (state.pieceAt(dest)) break;
          const promoted =
            piece.kind === 'man' && isPromotionSquare(dest, piece.color, size);
          out.push({ from: square, to: dest, captures: [], path: [dest], promoted });
        }
      }
    }
    return out;
  }

  private directionsForQuietMove(piece: Piece): readonly Direction[] {
    if (piece.kind === 'king') return ALL_DIAGONALS;
    const dr = piece.color === 'white' ? -1 : 1;
    return ALL_DIAGONALS.filter((d) => d.dr === dr);
  }
}
